import React, { useState, useEffect, useRef } from "react";
import "./AddContent.scss";
import { ReactComponent as UPloadLOGO } from "../../images/new_ui/bi_upload.svg";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { sidebarReducer } from "../../Redux/store";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Select, MenuItem, FormControl, Tooltip } from "@mui/material";
import config from '../../config.json'
const AddContent = (props) => {
  const { closeContentPopup } = props;
  const dispatch = useDispatch();
  const apiLoad = useSelector((state) => state.sidebar.apiLoad);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [fileCounts, setFileCounts] = useState({});
  const [fileType, setFileType] = useState("all");
  const [contentTypes, setContentTypes] = useState([]);
  const [allowedExtensions, setAllowedExtensions] = useState({});
  const [contentSubTypes, setContentSubTypes] = useState([]);

  const [selectedSubType, setSelectedSubType] = useState("");
  const [isFileInputDisabled, setIsFileInputDisabled] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const uploadAbortController = useRef(null);
  
  // Add this new state to track files with size errors
  const [fileSizeErrors, setFileSizeErrors] = useState({});

  const MAX_FILE_SIZE = config.MAX_FILE_SIZE || 1024 * 1024 * 1024;
  
  const filesizes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // Modify the useEffect that sets isButtonDisabled
  useEffect(() => {
    // Check if a tag (subType) is required but not selected
    const isTagRequired = fileType === "3dobject" && selectedFiles.length > 0;
    const isTagMissing = isTagRequired && !selectedSubType;
    
    // Check if any files exceed size limit
    const hasOversizedFiles = selectedFiles.some(file => file.size > MAX_FILE_SIZE);
  
    // Disable button if no files selected OR if tag is required but missing OR if any file is too large
    setIsButtonDisabled(selectedFiles.length === 0 || isTagMissing || hasOversizedFiles);
  
    // Keep the existing logic for file input disabling
    setIsFileInputDisabled(selectedFiles.length > 0);
  }, [selectedFiles, fileType, selectedSubType, fileSizeErrors]);

  const inputChange = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = [];
    const newPreviews = [];
    const newFileCounts = { ...fileCounts };
    const newFileSizeErrors = { ...fileSizeErrors };
  
    files.forEach((file) => {
      let fileName = file.name;
      if (newFileCounts[fileName]) {
        newFileCounts[fileName] += 1;
      } else {
        newFileCounts[fileName] = 1;
      }
  
      const [name, extension] = fileName.split(".");
      const count =
        newFileCounts[fileName] > 1 ? ` (${newFileCounts[fileName] - 1})` : "";
      fileName = `${name}${count}.${extension}`;
  
      // Add file size validation
      const newFile = new File([file], fileName, { type: file.type });
      newFiles.push(newFile);
      
      // Check if file size exceeds maximum and set error flag
      if (file.size > MAX_FILE_SIZE) {
        newFileSizeErrors[fileName] = true;
      } else {
        newFileSizeErrors[fileName] = false;
      }
  
      if (file.type.startsWith("image/")) {
        newPreviews.push({ type: "image", url: URL.createObjectURL(file) });
      } else if (file.type.startsWith("video/")) {
        newPreviews.push({ type: "video", url: URL.createObjectURL(file) });
      } else {
        newPreviews.push({ type: file.type, url: null });
      }
    });
  
    setSelectedFiles(selectedFiles.concat(newFiles));
    setPreviewImages(previewImages.concat(newPreviews));
    setFileCounts(newFileCounts);
    setFileSizeErrors({ ...fileSizeErrors, ...newFileSizeErrors });
  
    e.target.value = null;
  };
  
  const getFileIcon = (fileType) => {
    if (fileType.startsWith("audio/")) return "🎵";
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("doc") || fileType.includes("txt")) return "📝";
    if (fileType.includes("xls")) return "📊";
    if (fileType.includes("ppt")) return "📽️";
    if (["obj", "fbx", "glb", "gltf"].some((ext) => fileType.includes(ext)))
      return "🗿";
    if (
      fileType.includes("jpeg") ||
      fileType.includes("jpg") ||
      fileType.includes("png") ||
      fileType.includes("gif")
    )
      return "🖼️";
    if (
      fileType.includes("mp4") ||
      fileType.includes("avi") ||
      fileType.includes("mov") ||
      fileType.includes("mkv")
    )
      return "🎥";
    if (fileType.includes("odt")) return "📄";
    if (fileType.includes("bin") || fileType.includes("isp")) return "📦";
    if (fileType.includes("mp3")) return "🎵";
    if (fileType.includes("wrna")) return "📁";
    return "📁";
  };

  const deleteSelectedFile = (index) => {
    if (previewImages[index].url) {
      URL.revokeObjectURL(previewImages[index].url);
    }

    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);

    setSelectedFiles(newFiles);
    setPreviewImages(newPreviews);
    setIsButtonDisabled(newFiles.length === 0);

    // If no files remain, reset the subtype selection
    if (newFiles.length === 0) {
      setSelectedSubType("");
    }
  };

  const handleClose = () => {
    // Cancel any ongoing upload
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      console.log("Upload cancelled by user");
      
      // Reset upload progress and processing state
      setUploadProgress(0);
      setIsProcessing(false);
      
      // Show toast notification about cancellation
      toast.info("Upload cancelled", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored"
      });
    }
    
    // Call the original close function
    props.closeContentPopup();
  };

  const getFileExtension = (file) => {
    // First try to get extension from MIME type
    if (file.type) {
      // Map common MIME types to their appropriate extensions
      const mimeToExtMap = {
        'application/pdf': '.pdf',
        'application/msword': '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
        'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        'application/vnd.ms-powerpoint': '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
        'text/plain': '.txt',
        'image/jpeg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'video/mp4': '.mp4',
        'video/quicktime': '.mov',
        'video/x-msvideo': '.avi',
        'video/x-matroska': '.mkv',
        'audio/mpeg': '.mp3',
        'model/obj': '.obj',
        'model/gltf-binary': '.glb',
        'model/gltf+json': '.gltf'
        // Add more mappings as needed
      };
      
      if (mimeToExtMap[file.type]) {
        return mimeToExtMap[file.type];
      }
      
      // If not in our map but has a MIME type with subtype
      const parts = file.type.split('/');
      if (parts.length === 2 && parts[1] !== '') {
        // For general mime types that follow standard patterns
        if (!parts[1].includes('+')) {
          return `.${parts[1]}`;
        }
      }
    }
    
    // If MIME type doesn't provide useful info, try filename
    const filename = file.name.toLowerCase();
    
    // Common file extensions to check for
    const commonExtensions = ['.pdf', '.doc', '.docx', '.mp4', '.mp3', '.jpg', '.jpeg', '.png', 
                             '.gif', '.txt', '.xls', '.xlsx', '.ppt', '.pptx', '.obj', '.fbx', 
                             '.glb', '.gltf', '.avi', '.mov', '.mkv', '.odt', '.bin', '.isp', '.wrna'];
    
    // Check if the filename ends with any of the common extensions
    for (const ext of commonExtensions) {
      if (filename.endsWith(ext)) {
        return ext;
      }
    }
    
    // Fallback: get everything after the last period
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      return '.' + filename.substring(lastDotIndex + 1);
    }
    
    // If still no extension found, use a general extension based on the type
    if (file.type.startsWith('image/')) return '.img';
    if (file.type.startsWith('video/')) return '.vid';
    if (file.type.startsWith('audio/')) return '.aud';
    if (file.type.startsWith('text/')) return '.txt';
    
    // Last resort
    return '';
  };

  // Improved function to handle filenames with multiple periods
  const getProperFileName = (file) => {
    // Get the correct extension based on file type
    const correctExtension = getFileExtension(file);
    
    // Get the original filename
    const originalName = file.name;
    
    // Common extensions to check for (same as in getFileExtension)
    const commonExtensions = ['.pdf', '.doc', '.docx', '.mp4', '.mp3', '.jpg', '.jpeg', '.png', 
                             '.gif', '.txt', '.xls', '.xlsx', '.ppt', '.pptx', '.obj', '.fbx', 
                             '.glb', '.gltf', '.avi', '.mov', '.mkv', '.odt', '.bin', '.isp', '.wrna'];
    
    // Check if the file already ends with the correct extension
    if (originalName.toLowerCase().endsWith(correctExtension.toLowerCase())) {
      return originalName; // Already has correct extension
    }
    
    // Check if the file ends with any known extension
    for (const ext of commonExtensions) {
      if (originalName.toLowerCase().endsWith(ext.toLowerCase())) {
        // Replace existing extension with correct one
        return originalName.substring(0, originalName.length - ext.length) + correctExtension;
      }
    }
    
    // If no valid extension found in the name, simply add the correct extension
    // First check if there's any extension-like part at the end
    const lastDotIndex = originalName.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      const possibleExt = originalName.substring(lastDotIndex).toLowerCase();
      // Only replace what looks like an extension (not periods in the middle of name)
      if (possibleExt.length <= 6) { // Most extensions are short
        return originalName.substring(0, lastDotIndex) + correctExtension;
      }
    }
    
    // If no extension-like part found, just append the correct extension
    return originalName + correctExtension;
  };

  // New function to get content format (file extension without the dot)
  const getContentFormat = (file) => {
    const extension = getFileExtension(file);
    return extension.startsWith('.') ? extension.substring(1) : extension;
  };

  // New function to handle S3 upload with presigned URL
  const uploadToS3 = async (file, signedUrl) => {
    try {
      await axios.put(signedUrl, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(Math.min(percentCompleted, 95));
          if (percentCompleted >= 95) {
            setUploadProgress(95);
            setIsProcessing(true);
          } else {
            setUploadProgress(percentCompleted);
          }
        },
        signal: uploadAbortController.current?.signal
      });
      return true;
    } catch (error) {
      console.error("Error uploading to S3:", error);
      if (axios.isCancel(error)) {
        console.log("S3 upload cancelled");
        throw error; // Re-throw to be caught by caller
      }
      throw error;
    }
  };
  const getFormattedFileSize = (bytes) => {
    if (bytes === 0) return { size: 0, unit: "Bytes" };
    
    // 500KB threshold
    const KB_THRESHOLD = 500 * 1024;
    // 500MB threshold
    const MB_THRESHOLD = 500 * 1024 * 1024;
    
    if (bytes < KB_THRESHOLD) {
      // Less than 500KB, return in bytes
      return {
        size: bytes,
        unit: "Bytes"
      };
    } else if (bytes < MB_THRESHOLD) {
      // Between 500KB and 500MB, return in MB
      return {
        size: parseFloat((bytes / (1024 * 1024)).toFixed(2)),
        unit: "MB"
      };
    } else {
      // More than 500MB, return in GB
      return {
        size: parseFloat((bytes / (1024 * 1024 * 1024)).toFixed(2)),
        unit: "GB"
      };
    }
  };
  const fileUploadSubmit = async (e) => {
    e.preventDefault();

    // Check for oversized files first
    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      toast.error("Cannot upload files larger than 800MB", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return; // Exit function early to prevent upload
    }

    dispatch(sidebarReducer.apiLoad(true));
    setUploadProgress(0); // Reset progress when starting a new upload
    setIsProcessing(false);

    uploadAbortController.current = new AbortController();
    
    try {
      const userData = JSON.parse(sessionStorage.getItem("userdata"));
      const file = selectedFiles[0]; // Get the first file (current implementation handles one file at a time)
      const fileName = getProperFileName(file);
      const fileExtension = getFileExtension(file).replace(".", "");
      
      // Determine content type based on file extension
      let contentType;
      if (["obj", "fbx", "glb", "gltf"].includes(fileExtension)) {
        contentType = "3dobject";
      } else if (file.type.startsWith("application/") || fileExtension === "txt" || file.type === "text/plain") {
        contentType = "file";
      } else {
        contentType = file.type.split("/")[0];
      }
      
      // Prepare content sub type (only for 3D objects)
      const contentSubType = fileType === "3dobject" ? selectedSubType : "";
      const formattedSize = getFormattedFileSize(file.size);
      // Step 1: Get presigned URL from backend
      console.log("Requesting presigned URL with data:", {
        filename: fileName,
        contentType: fileType, 
        contentSubType: contentSubType,
        filsize: file.size
      });
      const formData = new FormData();
      // formData.append("content", file);
      formData.append("filename", fileName);
      formData.append("contentType", fileType);
      formData.append("contentSubType", contentSubType);
      formData.append("filsize", `${formattedSize.size} ${formattedSize.unit}`);

      
      const presignRes = await axios.post("/contentlogic/get-presigned-url", formData, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("access_token")}`
        }
      });
      
      // Extract signed URL and fileKey from response
      const { preSignedUrl, fileKey, contentfilepath } = presignRes.data;
      console.log("Received presigned URL:", preSignedUrl);
      console.log("File key:", fileKey);
      
      // Step 2: Upload file to S3 using the signed URL
      await uploadToS3(file, preSignedUrl);
      
      // Step 3: Notify backend of successful upload
      console.log("Updating metadata for uploaded file");
      const contentFormat = getContentFormat(file);
      const formDataContent = new FormData();
   formDataContent.append("teContentFileName", fileName);
      formDataContent.append("teContentName", fileName);
      formDataContent.append("teContentType", fileType);  
      formDataContent.append("teContentFormat", contentFormat);
      formDataContent.append("te_contentSubType", contentSubType);
      formDataContent.append("teContentDescription", `${fileType} uploaded by user ${userData.te_userid}`);
      formDataContent.append("te_companyid", userData.te_companyid);
      formDataContent.append("te_userid", userData.te_userid);
      formDataContent.append("te_ContentUrl", fileKey);
      // formDataContent.append("te_filesize", file.size);
      formDataContent.append("te_filesize", `${formattedSize.size} ${formattedSize.unit}`);
      formDataContent.append("te_contentfilepath", contentfilepath);
      
      // const metadataRes = await axios.post("/contentlogic/adds3metadata", formDataContent, {
      //   headers: {
      //     Authorization: `Bearer ${sessionStorage.getItem("access_token")}`
      //   }

      const metadataRes = await axios.post("/contentlogic/adds3metadata", formDataContent, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("access_token")}`
        }
      });
      
      setUploadProgress(100);
      // Add a small delay to ensure users see 100% before any UI changes
      await new Promise(resolve => setTimeout(resolve, 2000));
      setIsProcessing(false);
      
      dispatch(sidebarReducer.apiLoad(false));
      
      // Show success message
      toast.success("Content uploaded successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        onClose: () => closeContentPopup()
      });
      
    } catch (error) {
      console.log("Upload failed:", error);
      
      if (axios.isCancel(error)) {
        console.log("Request canceled", error);
        dispatch(sidebarReducer.apiLoad(false));
        return;
      }
      
      dispatch(sidebarReducer.apiLoad(false));
      setUploadProgress(0);
      setIsProcessing(false);
      
      // Show error message
      toast.error(error.response?.data?.message || error.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored"
      });
    } finally {
      // Clear the abort controller reference
      uploadAbortController.current = null;
    }
  };

  useEffect(() => {
    const fetchContentTypes = async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const response = await axios.get("/contenttype/listAll", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          const types = response.data.data;
       

          // Create content types array
          const formattedTypes = types.map((type) => ({
            value: type.te_contentType.toLowerCase(),
            label:
              type.te_contentType.charAt(0).toUpperCase() +
              type.te_contentType.slice(1),
            subTypes: type.te_contentSubType || [],
          }));

          setContentTypes(formattedTypes);

          // Set default fileType to the first content type
          if (formattedTypes.length > 0) {
            setFileType(formattedTypes[0].value);

            // If first type is 3dobject, set its subtypes
            const first3DObjectType = formattedTypes.find(
              (type) => type.value === "3dobject"
            );
            if (first3DObjectType) {
              setContentSubTypes(first3DObjectType.subTypes);
            }
          }

          // Create a map of allowed extensions
          const extensionsMap = types.reduce((acc, type) => {
            acc[type.te_contentType.toLowerCase()] = type.te_allowedExtensions
              .split(",")
              .map((ext) => `.${ext.trim()}`)
              .join(",");
            return acc;
          }, {});

          setAllowedExtensions(extensionsMap);
        } else {
          toast.error("Failed to fetch content types.");
        }
      } catch (error) {
        console.error("Error fetching content types:", error);
        toast.error("Error fetching content types.");
      }
    };

    fetchContentTypes();
  }, []);

  const getAcceptTypes = () => {
    return (
      allowedExtensions[fileType] ||
      "image/*,video/*,audio/*,.obj,.fbx,.glb,.gltf,.pdf,.doc,.docx,.ppt,.txt,.jpeg,.jpg,.png,.gif,.mp4,.avi,.mov,.mkv,.odt,.bin,.isp,.mp3,.wrna,.zip"
    );
  };

  const handleFileTypeChange = (event) => {
    const selectedType = event.target.value;
    setFileType(selectedType);

    clearSelectedFiles();

    setSelectedSubType("");

    // Update content subtypes if necessary
    const selectedTypeObj = contentTypes.find(
      (type) => type.value === selectedType
    );
    if (selectedTypeObj && selectedTypeObj.subTypes) {
      setContentSubTypes(selectedTypeObj.subTypes);
    } else {
      setContentSubTypes([]);
    }
  };
  
  const handleSubTypeSelect = (subType) => {
    setSelectedSubType(subType);
  };

  const clearSelectedFiles = () => {
    // Revoke object URLs to prevent memory leaks
    previewImages.forEach((preview) => {
      if (preview.url) {
        URL.revokeObjectURL(preview.url);
      }
    });

    setSelectedFiles([]);
    setPreviewImages([]);
    setFileCounts({});
    setIsButtonDisabled(true);
    setSelectedSubType("");
    setIsFileInputDisabled(false);
  };

  return (
    <div className="add-content">
      <ToastContainer closeButton={false} />
      {apiLoad ? (
        <div
          style={{
            height: "40vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div className="progress-container">
            <div
              className="progress-bar"
              style={{
                width: `${uploadProgress}%`,color:"#FFD119 !important"
              }}
            ></div>
            <div
              style={{
                textAlign: "center",
                marginTop: "5px",
                fontSize: "12px",
              }}
            >
              {uploadProgress}%
            </div>
          </div>
          <div>
            {isProcessing ? "Processing... Please Wait" : "Uploading... Please Wait"}
          </div>
        </div>
      ) : (
        <div className="fileupload-view">
          <div className="kb-data-box">
            <div className="kb-modal-data-title">
              <div className="kb-data-title">
                <h6>Add Content</h6>

                <FormControl className="content-dropdown">
                  <Tooltip
                    title={
                      selectedFiles.length > 0
                        ? "Please delete current file selection to change content type"
                        : ""
                    }
                  >
                    <Select
                      value={
                        contentTypes.some((ct) => ct.value === fileType)
                          ? fileType
                          : ""
                      }
                      onChange={handleFileTypeChange}
                      className="custom-select"
                      disabled={selectedFiles.length > 0}
                      sx={{
                        color: "#ecd577 !important", // This will change the text color
                        "& .MuiSvgIcon-root": {
                          // This targets the dropdown arrow
                          color: "#ecd577 !important", // This will change the arrow color
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-disabled": {
                          cursor: "not-allowed",
                          "& .MuiSelect-select": {
                            cursor: "not-allowed",
                            color: "rgba(0, 0, 0, 0.5)", // You can keep this or change it as needed
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                        },
                      }}
                    >
                      {contentTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Tooltip>
                </FormControl>
              </div>
            </div>

            <form onSubmit={fileUploadSubmit}>
              <div className="kb-file-upload">
                <div
                  className={`file-upload-box ${
                    isFileInputDisabled ? "disabled" : ""
                  }`}
                >
                  <label htmlFor="fileupload" className="file-upload-label">
                    <input
                      type="file"
                      id="fileupload"
                      className="file-upload-input"
                      onChange={inputChange}
                      accept={getAcceptTypes()}
                      disabled={isFileInputDisabled}
                      aria-label="Upload file"
                    />
                    <span>
                      Drag & Drop or{" "}
                      <span className="file-link">Choose your file</span>
                    </span>
                  
                  </label>
                </div>
                <div class="right-align">Please upload content up to 2 GB in size</div>
              </div>
              <div className="all-tags">
                {fileType === "3dobject" && contentSubTypes.length > 0 && (
                  <>
                    <h6 className="tags-heading">Tags</h6>
                    <div className="subtype-toolbar">
                      {contentSubTypes.map((subType) => (
                        <button
                          type="button"
                          key={subType}
                          className={`subtype-button ${
                            selectedSubType === subType ? "selected" : ""
                          }`}
                          onClick={() => handleSubTypeSelect(subType)}
                        >
                          {subType.charAt(0).toUpperCase() + subType.slice(1)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="kb-attach-box mb-3">
                {selectedFiles.map((file, index) => (
                  <div className="file-atc-box" key={index}>
                    <div className="file-detail">
                      {previewImages[index].type === "image" && (
                        <img
                          src={previewImages[index].url}
                          alt={`Preview ${index}`}
                          className="preview-image"
                        />
                      )}
                      {previewImages[index].type === "video" && (
                        <video
                          src={previewImages[index].url}
                          className="preview-video"
                          controls
                        />
                      )}
                      {!["image", "video"].includes(
                        previewImages[index].type
                      ) && (
                        <div className="preview-icon">
                          {getFileIcon(file.type)}
                        </div>
                      )}
                      <h6>{file.name}</h6>
                      <p>
                        <span>Size: {filesizes(file.size)}</span>
                      </p>
                      <div className="file-actions">
                        <button
                          type="button"
                          className="file-action-btn"
                          onClick={() => deleteSelectedFile(index)}
                        >
                          Delete
                        </button>
                      </div>
                      {file.size > MAX_FILE_SIZE && (
                        <div className="file-size-error">
                          File size exceeded Limit
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="kb-buttons-box">
                <span>
                  <Tooltip
                    title={
                      isButtonDisabled
                        ? selectedFiles.length === 0
                          ? "Choose any file"
                          : fileType === "3dobject" && !selectedSubType
                          ? "Select a tag"
                          : ""
                        : ""
                    }
                  >
                    <button
                      type="submit"
                      className={`form-submit ${
                        isButtonDisabled ? "disabled" : ""
                      }`}
                      disabled={isButtonDisabled}
                    >
                      <UPloadLOGO style={{ width: "20px", height: "auto" }} />{" "}
                      Upload
                    </button>
                  </Tooltip>
                </span>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddContent;